import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  withRouter,
  ActionList,
  BreadcrumbBar,
  IBreadcrumbBarItem,
  IIconTextLinkProps,
  Head,
  myAccountLink,
  RouterProps,
} from "component-library";
import {
  FEATURE_FLAGS,
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
} from "shared-services";
import { withTranslation, WithTranslation } from "i18n";
import { IBreakdown } from "shared/models/breakdown";
import { FacetCategory } from "shared/models/facet";
import { IUser } from "shared/models/user";

import {
  MY_FILTERS_FULL_PATH,
  PageTitleKey,
  USE_BROWSER_BACK_KEY,
} from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { getFacets } from "store/facet/facet.actions";
import { FacetStoreKey, GetFacets } from "store/facet/facet.types";
import { getCountries } from "store/location/location.actions";
import { GetCountries } from "store/location/location.types";
import { getRolePage } from "store/page/page.actions";
import { GetRolePage } from "store/page/page.types";
import { getPageTranslation } from "store/translation/translation.actions";
import {
  GetPageTranslation,
  ITranslationState,
} from "store/translation/translation.types";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, ShowPopup } from "store/ui/ui.types";

import { IActionList, IActionListItem } from "models/common";
import { JobItemType } from "models/job";

import { IPrepareBreakdownViewData } from "helpers/breakdownViewHelpers";
import copyPrefixedObjectKeys from "helpers/copyPrefixedObjectKeys";
import { getPageTitle } from "helpers/getPageTitle";
import jobService, { SetJobActionType } from "services/api/job/jobService";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withPageData, {
  ILoaders,
  IWithPageDataProps,
  mapStateToWithPageDataProps,
} from "hoc/withPageData";

import BreakdownView, {
  IBreakdownViewRole,
  IBreakdownViewTexts,
} from "components/BreakdownView/BreakdownView";

import {
  ActionListItemKey,
  prepareActionList,
  prepareBreadcrumbs,
  prepareBreakdownViewRoles,
  prepareBreakdownViewTexts,
} from "./RolePage.helpers";
import { setJobsFeedData } from "store/jobsFeed/jobsFeed.actions";
import { SetJobsFeedData, IJobsFeedState } from "store/jobsFeed/jobsFeed.types";

import "./RolePage.scss";

export interface IRolePageActions {
  getPageTranslation: GetPageTranslation;
  getRolePage: GetRolePage;
  getCountries: GetCountries;
  getFacets: GetFacets;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  setJobsFeedData: SetJobsFeedData;
}

export interface IRolePageStoreProps {
  translation: ITranslationState;
  breakdown: IBreakdown;
  breadcrumbs: IBreadcrumbBarItem[];
  breakdownViewTexts: IBreakdownViewTexts;
  breakdownRoles: IBreakdownViewRole[];
  pageTitle: string;
  user: IUser;
  markAsRead: SetJobActionType;
  actionList: IActionList;
  jobsFeed: IJobsFeedState;
}

export interface IRolePageProps
  extends
    IRolePageActions,
    IRolePageStoreProps,
    IWithPageDataProps,
    RouterProps,
    WithTranslation,
    LaunchDarklyClientWithFlags {}

interface IRolePageState {
  returnUri?: string;
  [USE_BROWSER_BACK_KEY]?: boolean;
}

export class RolePage extends React.Component<IRolePageProps, IRolePageState> {
  private readonly actionListHandlers: {
    [ActionListItemKey.BackToJobsFeed](): void;
  };

  readonly state: Readonly<IRolePageState> = {};

  constructor(props: IRolePageProps) {
    super(props);

    const actionListHandlers = {
      [ActionListItemKey.BackToJobsFeed]: this.handleBackClick,
    };
    this.actionListHandlers = copyPrefixedObjectKeys(
      actionListHandlers,
      "more-dropdown_"
    );
  }

  public componentDidMount() {
    void this.markAsRead();
    this.setReturnUri();
  }

  public render() {
    const {
      breadcrumbs,
      breakdown,
      breakdownRoles,
      breakdownViewTexts,
      user,
      actionList,
    } = this.props;

    return (
      <section className="c-role-page">
        <Head registerPageView={this.registerPageView}>
          <title>{this.props.pageTitle}</title>
        </Head>
        <BreadcrumbBar items={breadcrumbs} />
        <BreakdownView
          breakdown={breakdown}
          roles={breakdownRoles}
          texts={breakdownViewTexts}
          limitWidth={true}
          user={user}
          actionList={
            <div>
              <ActionList
                items={this.prepareActionListItems()}
                moreButtonText={actionList.moreButtonText}
                variant="pageHeader"
              />
            </div>
          }
        />
      </section>
    );
  }

  private handleBackClick = () => {
    const { router } = this.props;
    const { returnUri, [USE_BROWSER_BACK_KEY]: useBrowserBack } = this.state;

    const alternativePath =
      this.props.jobsFeed.pathname ?? MY_FILTERS_FULL_PATH;

    this.props.setJobsFeedData({
      pathname: this.props.jobsFeed.pathname,
      pageNumber: this.props.jobsFeed.pageNumber,
      breakdownId: this.props.jobsFeed.breakdownId,
      isBackToJobsFeed: true,
    });

    if (useBrowserBack) {
      router.navigate(-1);
    } else {
      router.navigate(returnUri || alternativePath);
    }
  };

  private createHandlerActionListItemClick =
    (id: string = "") =>
    (e: React.MouseEvent) => {
      e.preventDefault();

      this.actionListHandlers[id]?.();
    };

  private prepareActionListItems(): IIconTextLinkProps[] {
    return this.props.actionList.items?.map(
      (item: IActionListItem): IIconTextLinkProps => ({
        ...item,
        handleClick: this.createHandlerActionListItemClick(item.id),
      })
    );
  }

  private async markAsRead() {
    const id = jobService.generateInteractionItemId(
      JobItemType.Role,
      this.props.breakdown.id,
      this.props.breakdownRoles[0].id
    );
    try {
      await this.props.markAsRead([{ id, jobItemType: JobItemType.Role }]);
    } catch (e) {
      console.error(e);
    }
  }

  private registerPageView = () => {
    GTMEvents.jobsFeed.pageViews.role(this.props.pageTitle);
  };

  private setReturnUri() {
    const { router } = this.props;
    const [searchParams, setSearchParams] = router.searchParams;

    this.setState({
      returnUri: searchParams.get("returnUri") || "",
      [USE_BROWSER_BACK_KEY]: !!router.location.state?.from,
    });
    setSearchParams("", { replace: true });
  }
}

async function loadExternalData(props: IRolePageProps) {
  return Promise.all([
    props.getCountries(props.locale),
    props.getFacets(
      [
        FacetCategory.ContractType,
        FacetCategory.ProductionType,
        FacetCategory.Gender,
        FacetCategory.Appearance,
        FacetCategory.Disability,
        FacetCategory.ComplaintCategory,
      ],
      props.locale
    ),
  ]);
}

const pageDataLoaders: ILoaders<IRolePageProps> = {
  loadTranslation(props, isLocaleChanged) {
    return props.getPageTranslation(
      { pageKey: PageKey.Role, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );
  },
  async loadData(props) {
    const { breakdownId = "", roleId = "" } = props.router.params;

    await loadExternalData(props);

    return props.getRolePage(
      breakdownId.toLowerCase(),
      roleId.toLowerCase(),
      props.abortController
    );
  },
  loadOnLocaleChange: loadExternalData,
};

const mapStateToProps = (
  state: IApplicationState,
  props: IRolePageProps
): IRolePageStoreProps => {
  const { t, flags } = props;
  const isYPPlayingAgeEnabled = flags[FEATURE_FLAGS.YPsPlayingAgeMin];
  const pageTranslation = state.translation.pages[PageKey.Role]!;
  const commonTranslation = state.translation.common;
  const breakdown = state.page.data!.breakdown!;
  const prepareBreakdownViewData: IPrepareBreakdownViewData = {
    productionTypes: state.facet[FacetStoreKey.ProductionType]!,
    genders: state.facet[FacetStoreKey.Gender]!,
    disabilities: state.facet[FacetStoreKey.Disability]!,
    appearances: state.facet[FacetStoreKey.Appearance]!,
    contractTypes: state.facet[FacetStoreKey.ContractType]!,
    countries: state.location.countries!,
  };
  const breakdownRoles = prepareBreakdownViewRoles({
    pageTranslation,
    breakdown,
    prepareBreakdownViewData,
    t,
    isYPPlayingAgeEnabled,
  });

  return {
    user: state.user.data!,
    translation: state.translation,
    breakdown,
    breadcrumbs: prepareBreadcrumbs(
      pageTranslation,
      myAccountLink,
      props.router.navigate
    ),
    breakdownViewTexts: prepareBreakdownViewTexts(
      pageTranslation,
      state.translation.common,
      breakdown,
      prepareBreakdownViewData
    ),
    actionList: prepareActionList(pageTranslation),
    breakdownRoles,
    pageTitle: getPageTitle(commonTranslation, PageTitleKey.Role, (format) =>
      format.replace("{roleName}", breakdown.roles[0].role.name)
    ),
    jobsFeed: state.jobsFeed,
    markAsRead: (...args) => jobService.markAsRead(...args),
  };
};

const mapDispatchToProps = {
  setLoading,
  getPageTranslation,
  getRolePage,
  getCountries,
  getFacets,
  showPopup,
  hidePopup,
  setJobsFeedData,
};

export default compose(
  withRouter,
  withTranslation(),
  withLaunchDarklyConsumerHOC(),
  connect(mapStateToWithPageDataProps, mapDispatchToProps),
  withPageData<IRolePageProps>(PageKey.Role, pageDataLoaders),
  connect(mapStateToProps, mapDispatchToProps)
)(RolePage) as React.ComponentType;
